import type { InteractionTargetMetadata } from "./types";

// DOM helpers for turning click targets into compact interaction metadata.
// Selectors used to decide which clicks are worth monitoring.
const INTERACTIVE_SELECTOR = [
  "a[href]",
  "button",
  "summary",
  "input[type='button']",
  "input[type='submit']",
  "input[type='reset']",
  "[role='button']",
  "[role='link']",
  "[data-monitor-click]",
].join(", ");

// Compresses visible text into a short label that is safe to emit.
function getTextPreview(value: string): string {
  return value.replace(/\s+/g, " ").trim().slice(0, 80);
}

// Picks the best user-facing label available for the clicked element.
function getElementLabel(element: HTMLElement): string {
  const ariaLabel = element.getAttribute("aria-label");
  if (ariaLabel) {
    return getTextPreview(ariaLabel);
  }

  const title = element.getAttribute("title");
  if (title) {
    return getTextPreview(title);
  }

  const textContent = element.textContent;
  if (textContent) {
    return getTextPreview(textContent);
  }

  return "";
}

// Builds a lightweight selector string to help identify the target later.
function getElementSelector(element: HTMLElement): string {
  const id = element.id ? `#${element.id}` : "";
  const role = element.getAttribute("role");
  const roleSegment = role ? `[role="${role}"]` : "";
  const dataTestId = element.getAttribute("data-testid");
  const testIdSegment = dataTestId ? `[data-testid="${dataTestId}"]` : "";

  return `${element.tagName.toLowerCase()}${id}${roleSegment}${testIdSegment}`;
}

// Extracts navigation details from the nearest anchor so redirects can be tracked.
function getHrefDetails(element: HTMLElement): {
  href: string;
  path: string;
  navigationCandidate: boolean;
} {
  const anchor = element.closest("a[href]") as HTMLAnchorElement | null;

  if (!anchor?.href) {
    return {
      href: "",
      path: "",
      navigationCandidate: false,
    };
  }

  try {
    const url = new URL(anchor.href, window.location.href);
    return {
      href: anchor.href,
      path: `${url.pathname}${url.search}${url.hash}`,
      navigationCandidate: true,
    };
  } catch {
    return {
      href: anchor.href,
      path: anchor.getAttribute("href") ?? "",
      navigationCandidate: true,
    };
  }
}

// Resolves a DOM event target into the metadata used by interaction monitoring.
export function getTrackableTarget(target: EventTarget | null): InteractionTargetMetadata | null {
  if (!(target instanceof HTMLElement)) {
    return null;
  }

  const interactiveElement = target.closest(INTERACTIVE_SELECTOR) as HTMLElement | null;

  if (!interactiveElement) {
    return null;
  }

  const tagName = interactiveElement.tagName.toLowerCase();
  const role = interactiveElement.getAttribute("role") ?? "";
  const label = getElementLabel(interactiveElement);
  const selector = getElementSelector(interactiveElement);
  const { href, path, navigationCandidate } = getHrefDetails(interactiveElement);
  const signature = [tagName, role, selector, href || path || label].filter(Boolean).join("|");

  return {
    signature,
    tagName,
    role,
    label,
    href,
    path,
    selector,
    navigationCandidate,
  };
}
